// feature/moderation-escalation.
// Moderation queue for the insumos team + admin/directora. Renders pending
// moderation_requests; resolution modal handles the five outcomes. After
// resolve, refreshes the list in place (no navigation away).

import React, { useEffect, useMemo, useState } from "react";
import Spinner from "../../components/Spinner";
import Modal from "../../components/Modal";
import { TipoPill, EstadoPill, SlaPill } from "../../components/Pills";
import { moderation as moderationApi, users as usersApi } from "../../api/endpoints";
import { useToast } from "../../components/Toast";
import { timeAgo } from "../../components/timeAgo";
import CaseDetail from "../case-detail";

const fmt = (d) => d ? new Date(d).toLocaleString("es-CO") : "—";

export default function ModerationQueue() {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(null);   // request being resolved
  const [openCaseId, setOpenCaseId] = useState(null);

  function load() {
    setData(null); setError(null);
    moderationApi.queue()
      .then((r) => setData(r.data?.requests || []))
      .catch((e) => setError(e?.response?.data?.error || "No se pudo cargar la cola."));
  }
  useEffect(() => { load(); }, []);

  if (error) {
    return <div className="card tight" style={{ padding: 16, color: "var(--color-danger)" }}>{error}</div>;
  }
  if (!data) {
    return <div style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>;
  }

  return (
    <div className="col" style={{ gap: 16 }}>
      <header>
        <h1 style={{ margin: 0 }}>Cola de moderación</h1>
        <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
          Solicitudes de abogados que necesitan revisión por insumos atrasados u otros bloqueos.
        </div>
      </header>

      {data.length === 0 ? (
        <div className="card tight" style={{ padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 14 }}>No hay solicitudes pendientes.</div>
        </div>
      ) : (
        <div className="col" style={{ gap: 12 }}>
          {data.map((r) => (
            <ModerationCard key={r.id} req={r}
                            onOpenCase={() => setOpenCaseId(r.case_id)}
                            onResolve={() => setActive(r)} />
          ))}
        </div>
      )}

      {active && (
        <ResolveModal
          req={active}
          onClose={() => setActive(null)}
          onResolved={() => { setActive(null); load(); toast.push("Resuelto."); }}
        />
      )}

      {openCaseId && (
        <CaseDetail
          caseId={openCaseId}
          open={!!openCaseId}
          onClose={() => setOpenCaseId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function ModerationCard({ req, onOpenCase, onResolve }) {
  return (
    <div className="card" style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button className="ghost sm" onClick={onOpenCase} style={{ fontWeight: 600 }}>{req.case_number}</button>
        <TipoPill tipo={req.tipo} />
        <EstadoPill estado={req.estado} sub_tipo={req.sub_tipo} />
        <SlaPill c={req} />
        <span className="muted" style={{ fontSize: 12, marginLeft: "auto" }}>
          {timeAgo(req.requested_at)}
        </span>
      </div>
      <div style={{ fontSize: 13 }}>
        <strong>{req.accionante || "—"}</strong>
        {req.radicado && <span className="muted" style={{ marginLeft: 8, fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace", fontSize: 11 }}>· rad. {req.radicado}</span>}
      </div>
      <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
        Solicitado por: <strong>{req.requested_by_name || req.requested_by_email}</strong>
        {req.abogado_name && req.abogado_email !== req.requested_by_email && (
          <> · abogado responsable: <strong>{req.abogado_name}</strong></>
        )}
      </div>
      <div style={{
        background: "var(--bg-subtle)", padding: 10,
        borderRadius: 4, fontSize: 13, whiteSpace: "pre-wrap",
      }}>{req.reason}</div>
      {(req.lawyer_deadline_at || req.insumos_deadline_at) && (
        <div className="muted" style={{ fontSize: 11, display: "flex", gap: 16 }}>
          {req.lawyer_deadline_at && <span>Plazo abogado: {fmt(req.lawyer_deadline_at)}</span>}
          {req.insumos_deadline_at && <span>Plazo insumos: {fmt(req.insumos_deadline_at)}</span>}
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button className="secondary sm" onClick={onOpenCase}>Ver caso</button>
        <button className="sm" onClick={onResolve}>Resolver</button>
      </div>
    </div>
  );
}

const RESOLUTION_LABELS = {
  extend_deadline:                "Extender plazo del abogado",
  mark_insumo_deficiente_advance: "Marcar insumo deficiente — avanzar sin él",
  reassign_lawyer:                "Reasignar a otro abogado",
  confirm_vencida:                "Confirmar vencida (Tania revisará)",
  reject_request:                 "Rechazar solicitud",
};

function ResolveModal({ req, onClose, onResolved }) {
  const toast = useToast();
  const [resolution, setResolution] = useState("");
  const [notes, setNotes]           = useState("");
  const [newDeadline, setNewDeadline] = useState("");
  const [reassignTo, setReassignTo] = useState("");
  const [lawyers, setLawyers]       = useState([]);
  const [busy, setBusy]             = useState(false);

  // Lazy-load eligible lawyers (filtered by case tipo) when reassign is picked.
  useEffect(() => {
    if (resolution !== "reassign_lawyer") return;
    if (lawyers.length) return;
    usersApi.list({ role: "abogado", case_tipo: req.tipo })
      .then((r) => setLawyers((r.data || []).filter((u) => u.active && u.id !== req.abogado_asignado_id)))
      .catch(() => toast.push("No se pudo cargar abogados", "error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolution]);

  const ok = useMemo(() => {
    if (!resolution) return false;
    if (resolution === "extend_deadline" && !newDeadline) return false;
    if (resolution === "reassign_lawyer" && !reassignTo)  return false;
    return true;
  }, [resolution, newDeadline, reassignTo]);

  async function submit() {
    if (!ok) return;
    setBusy(true);
    try {
      const body = {
        resolution,
        notes: notes.trim() || null,
        new_deadline_at: resolution === "extend_deadline"
          ? new Date(newDeadline).toISOString()
          : null,
        reassigned_to_user_id: resolution === "reassign_lawyer"
          ? Number(reassignTo)
          : null,
      };
      await moderationApi.resolve(req.id, body);
      onResolved();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al resolver", "error");
    } finally { setBusy(false); }
  }

  return (
    <Modal
      open={true}
      title={`Resolver solicitud — ${req.case_number}`}
      onClose={busy ? undefined : onClose}
      footer={<>
        <button className="secondary" disabled={busy} onClick={onClose}>Cancelar</button>
        <button disabled={!ok || busy} onClick={submit}>
          {busy && <Spinner color="currentColor" />} Confirmar
        </button>
      </>}
    >
      <div className="card tight" style={{ padding: 10, fontSize: 13, marginBottom: 12 }}>
        <div><strong>Tipo:</strong> {req.tipo}</div>
        <div><strong>Estado:</strong> {req.estado}</div>
        <div><strong>Accionante:</strong> {req.accionante || "—"}</div>
        <div><strong>Razón del abogado:</strong></div>
        <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{req.reason}</div>
      </div>

      <label style={{ display: "block", textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
        Decisión <span style={{ color: "var(--color-warning)" }}>*</span>
        <select value={resolution} onChange={(e) => setResolution(e.target.value)}
                disabled={busy} style={{ marginTop: 4, width: "100%" }} autoFocus>
          <option value="">— Elegir —</option>
          {Object.entries(RESOLUTION_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </label>

      {resolution === "extend_deadline" && (
        <label style={{ marginTop: 12, display: "block", textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
          Nuevo plazo del abogado <span style={{ color: "var(--color-warning)" }}>*</span>
          <input type="datetime-local" value={newDeadline}
                 onChange={(e) => setNewDeadline(e.target.value)}
                 disabled={busy} style={{ marginTop: 4, width: "100%" }} />
        </label>
      )}

      {resolution === "reassign_lawyer" && (
        <label style={{ marginTop: 12, display: "block", textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
          Reasignar a <span style={{ color: "var(--color-warning)" }}>*</span>
          <select value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}
                  disabled={busy} style={{ marginTop: 4, width: "100%" }}>
            <option value="">— Elegir abogado —</option>
            {lawyers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name || u.email}
                {u.specializations?.length ? ` · ${u.specializations.join(", ")}` : ""}
              </option>
            ))}
          </select>
          {lawyers.length === 0 && (
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>Cargando o sin abogados disponibles…</div>
          )}
        </label>
      )}

      <label style={{ marginTop: 12, display: "block", textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
        Notas
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                  rows={3} maxLength={1000} disabled={busy}
                  placeholder="Detalles de la decisión que verá el abogado…"
                  style={{ marginTop: 4, width: "100%" }} />
      </label>
    </Modal>
  );
}
